import { BuildxConfig } from "./types";
import { Auth } from "./services/Auth";
import { Projects } from "./services/Projects";
import { Collections } from "./services/Collections";
import { Storage } from "./services/Storage";
import { Flows } from "./services/Flows";
import { Templates } from "./services/Templates";
import { Functions } from "./services/Functions";
import { BuildxObjects } from "./services/BuildxObjects";
import { ApiKeys } from "./services/ApiKeys";

/**
 * Main Buildx SDK class
 * 
 * @example
 * ```typescript
 * import { Buildx } from 'buildx-connect';
 * 
 * const buildx = new Buildx({
 *   apiEndpoint: 'https://api.buildx.com',
 *   apiKey: 'your-api-key',
 *   projectId: 'your-project-id'
 * });
 * 
 * // Use different services
 * const auth = buildx.auth();
 * const projects = buildx.projects();
 * const collections = buildx.collections();
 * ```
 */
export class Buildx {
	private config: BuildxConfig;
	private _auth: Auth;
	private _projects: Projects;
	private _collections: Collections;
	private _storage: Storage;
	private _flows: Flows;
	private _templates: Templates;
	private _functions: Functions;
	private _buildxObjects: BuildxObjects;
	private _apiKeys: ApiKeys;

	constructor(config: BuildxConfig) {
		this.config = {
			apiEndpoint: config.apiEndpoint.replace(/\/$/, ""), // Remove trailing slash
			apiKey: config.apiKey,
			projectId: config.projectId,
			organizationId: config.organizationId,
		};

		// Initialize services
		this._auth = new Auth(this.config);
		this._projects = new Projects(this.config);
		this._collections = new Collections(this.config);
		this._storage = new Storage(this.config);
		this._flows = new Flows(this.config);
		this._templates = new Templates(this.config);
		this._functions = new Functions(this.config);
		this._buildxObjects = new BuildxObjects(this.config);
		this._apiKeys = new ApiKeys(this.config);
	}

	/**
	 * Get the current configuration
	 */
	getConfig(): BuildxConfig {
		return { ...this.config };
	}

	/**
	 * Update the configuration
	 */
	updateConfig(config: Partial<BuildxConfig>): void {
		this.config = { ...this.config, ...config };

		// Update all services with new config
		this._auth.updateConfig(this.config);
		this._projects.updateConfig(this.config);
		this._collections.updateConfig(this.config);
		this._storage.updateConfig(this.config);
		this._flows.updateConfig(this.config);
		this._templates.updateConfig(this.config);
		this._functions.updateConfig(this.config);
		this._buildxObjects.updateConfig(this.config);
		this._apiKeys.updateConfig(this.config);
	}

	/**
	 * Authentication service
	 * Handles user authentication, login, signup, and user management
	 */
	auth(): Auth {
		return this._auth;
	}

	/**
	 * Projects service
	 * Manages projects, including CRUD operations
	 */
	projects(): Projects {
		return this._projects;
	}

	/**
	 * Collections service
	 * Manages data collections, documents, and queries
	 */
	collections(): Collections {
		return this._collections;
	}

	/**
	 * Storage service
	 * Handles file upload, download, and management
	 */
	storage(): Storage {
		return this._storage;
	}

	/**
	 * Flows service
	 * Manages workflow execution and flow types
	 */
	flows(): Flows {
		return this._flows;
	}

	/**
	 * Templates service
	 * Handles template rendering and preview
	 */
	templates(): Templates {
		return this._templates;
	}

	/**
	 * Functions service
	 * Manages serverless functions
	 */
	functions(): Functions {
		return this._functions;
	}

	/**
	 * Buildx Objects service
	 * Manages Buildx-specific objects (users, roles, etc.)
	 */
	buildxObjects(): BuildxObjects {
		return this._buildxObjects;
	}

	/**
	 * API Keys service
	 * Manages API key operations
	 */
	apiKeys(): ApiKeys {
		return this._apiKeys;
	}
}

// Export the main class as default
export default Buildx;